import type {
  SlimSupplierFilter,
  SupplierFilter,
} from '../../../insights/suppliers/models/supplier.models';
import { isNumber } from '../is-number/is-number.helper';
import { isRecord } from '../is-record/is-record.helper';
import { isString } from '../is-string/is-string.helper';

/**
 * Transform an enriched SupplierFilter into a SlimSupplierFilter payload for the API.
 *
 * Preserves: id, module, uniqueId, label, relation, name, block_field_id (with normalization), and block_field.relation.
 *
 * @param filter Enriched supplier filter.
 * @returns Slim filter suitable for transmission.
 */
export const slimSupplierFilter = (
  filter: SupplierFilter,
): SlimSupplierFilter => {
  const blockField = filter.block_field;

  const relationSource = filter.relation;

  let relation: string | Record<string, unknown> = '';
  if (isString(relationSource) || isRecord(relationSource)) {
    relation = relationSource;
  }

  const module = filter.module;

  const name = pickFirstString(
    [filter.name, blockField?.field_name],
    `filter_${filter.id}`,
  );

  const label = pickFirstString(
    [filter.personalizedLabel ?? undefined, filter.label, blockField?.label],
    name,
  );
  const blockFieldId = normalizeBlockFieldId(
    filter.block_field_id,
    blockField?.id,
  );

  return {
    id: filter.id,
    module,
    uniqueId: filter.uniqueId,
    label,
    relation,
    name,
    block_field_id: blockFieldId,
    block_field: {
      relation,
    },
  };
};

/**
 * Returns the first non-empty string from candidates, or fallback.
 *
 * @param candidates Array of potential string values
 * @param fallback Default value if no valid candidate found
 * @returns First non-empty trimmed string, or fallback
 */
const pickFirstString = (
  candidates: Array<string | undefined>,
  fallback: string,
): string => {
  for (const candidate of candidates) {
    if (isString(candidate) && candidate.trim().length > 0) {
      return candidate;
    }
  }

  return fallback;
};

/**
 * Normalizes block_field_id from string or number format.
 *
 * @param primary Primary source (filter.block_field_id)
 * @param fallback Fallback source (filter.block_field.id)
 * @returns Numeric block_field_id, or 0 if parsing fails
 */
const normalizeBlockFieldId = (primary: unknown, fallback: unknown): number => {
  const values = [primary, fallback];

  for (const value of values) {
    if (isNumber(value)) {
      return value;
    }

    if (isString(value)) {
      const parsed = Number.parseInt(value, 10);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
  }

  return 0;
};
